import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAuth } from '../../contexts/AuthContext';
import { calculateMortgagePayment, generateAmortizationSchedule } from '../../utils/mortgageCalculations';

// Helper function to round to nearest cent (matches mortgageCalculations.js)
const roundToCent = (value) => {
  return Math.round(value * 100) / 100;
};

const MortgageCalculator = () => {
  const navigate = useNavigate();
  const { formatCurrency, currency } = useCurrency();
  const { userProfile } = useAuth();

  // Payments should display cents (bank-style)
  const formatCurrencyWithCents = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })?.format(value);
  };
  const [formData, setFormData] = useState({
    purchasePrice: '',
    downPaymentPercent: '20',
    interestRate: '',
    amortizationPeriod: '25',
    paymentFrequency: 'monthly',
    schlPremium: ''
  });

  const paymentFrequencies = [
    { value: 'monthly', label: 'Mensuel' },
    { value: 'biweekly', label: 'Aux 2 semaines' },
    { value: 'weekly', label: 'Hebdomadaire' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate results automatically when form data changes
  const results = useMemo(() => {
    // Parse inputs - no fallbacks, use exact values from form
    const purchasePrice = parseFloat(formData.purchasePrice);
    const downPaymentPercent = parseFloat(formData.downPaymentPercent);
    const interestRate = parseFloat(formData.interestRate);
    const amortizationYears = parseInt(formData.amortizationPeriod);
    const paymentFrequency = formData.paymentFrequency;

    // Only calculate if we have all required values (no defaults)
    if (
      !formData.purchasePrice || 
      !formData.interestRate || 
      !formData.amortizationPeriod ||
      !formData.paymentFrequency ||
      isNaN(purchasePrice) || 
      isNaN(interestRate) || 
      isNaN(amortizationYears) ||
      purchasePrice <= 0 || 
      interestRate <= 0 ||
      amortizationYears <= 0
    ) {
      return null;
    }

    // Calculate derived values from exact inputs
    const downPaymentAmount = purchasePrice * ((downPaymentPercent || 0) / 100);
    const baseMortgageAmount = purchasePrice - downPaymentAmount;
    
    // Calculate SCHL premium if applicable (percentage of base mortgage amount)
    const schlPremiumPercent = parseFloat(formData.schlPremium) || 0;
    const schlPremiumAmount = schlPremiumPercent > 0 ? (baseMortgageAmount * (schlPremiumPercent / 100)) : 0;
    
    // Final mortgage amount includes SCHL premium
    const mortgageAmount = baseMortgageAmount + schlPremiumAmount;

    if (mortgageAmount <= 0) {
      return null;
    }

    // Use utility function for mortgage payment calculation (handles Canadian semi-annual compounding)
    // Use EXACT values: mortgageAmount, interestRate, amortizationYears, paymentFrequency
    const monthlyCalc = calculateMortgagePayment(mortgageAmount, interestRate, amortizationYears, 'monthly');
    const biweeklyCalc = calculateMortgagePayment(mortgageAmount, interestRate, amortizationYears, 'biweekly');
    const weeklyCalc = calculateMortgagePayment(mortgageAmount, interestRate, amortizationYears, 'weekly');

    // Calculate payments for different frequencies
    let paymentAmounts = {
      monthly: monthlyCalc.periodicPayment,
      biweekly: biweeklyCalc.periodicPayment,
      weekly: weeklyCalc.periodicPayment
    };

    // Get selected calculation based on exact paymentFrequency (no fallback)
    let selectedCalc;
    if (paymentFrequency === 'weekly') {
      selectedCalc = weeklyCalc;
    } else if (paymentFrequency === 'biweekly') {
      selectedCalc = biweeklyCalc;
    } else {
      selectedCalc = monthlyCalc;
    }

    const selectedPayment = paymentAmounts[paymentFrequency] || selectedCalc.periodicPayment;

    // Calculate amortization schedule using EXACT same values as displayed
    // Source of truth: mortgageAmount, interestRate, amortizationYears, paymentFrequency
    const amortizationSchedule = generateAmortizationSchedule(
      mortgageAmount,
      interestRate,
      amortizationYears,
      paymentFrequency
    );

    // Calculate totals from the schedule (source of truth - matches bank calculators exactly)
    // Sum all interest and principal payments from the schedule to get exact totals
    let totalInterestFromSchedule = 0;
    let totalPrincipalFromSchedule = 0;
    
    if (amortizationSchedule && amortizationSchedule.length > 0) {
      amortizationSchedule.forEach(year => {
        totalInterestFromSchedule = roundToCent(totalInterestFromSchedule + (year.interestPaid || 0));
        totalPrincipalFromSchedule = roundToCent(totalPrincipalFromSchedule + (year.principalPaid || 0));
      });
    }
    
    const totalAmount = roundToCent(totalInterestFromSchedule + totalPrincipalFromSchedule);
    const totalInterest = roundToCent(totalInterestFromSchedule);

    // Log the source values used for amortization calculation
    console.log("AMORT SOURCE CHECK", {
      periodicRate: selectedCalc?.periodicRate?.toPrecision?.(12),
      payment: selectedCalc?.periodicPayment?.toFixed?.(2),
      mortgageAmount,
      baseMortgageAmount,
      schlPremiumAmount,
      interestRate,
      amortizationYears,
      paymentFrequency,
      totalAmountFromSchedule: totalAmount,
      totalInterestFromSchedule: totalInterest
    });

    return {
      purchasePrice,
      downPaymentAmount,
      baseMortgageAmount,
      schlPremiumAmount,
      mortgageAmount,
      monthlyPayment: selectedPayment, // Use selected frequency payment
      paymentAmounts,
      totalInterest,
      totalAmount,
      amortizationYears,
      amortizationSchedule
    };
  }, [formData.purchasePrice, formData.downPaymentPercent, formData.interestRate, formData.amortizationPeriod, formData.paymentFrequency, formData.schlPremium]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
      <NavigationHeader user={userProfile} subscriptionTier={userProfile?.subscription_tier || 'free'} />
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: 'var(--spacing-lg) var(--spacing-md)',
        paddingTop: 'calc(64px + var(--spacing-lg))'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            onClick={() => navigate('/main-dashboard')}
          >
            Retour
          </Button>
          <h1 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--color-foreground)'
          }}>
            Calculateur hypothécaire
          </h1>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 'var(--spacing-lg)'
        }}
        className="md:grid-cols-2"
        >
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            padding: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'var(--color-primary)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="Calculator" size={24} color="white" />
              </div>
              <div>
                <h2 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: 'var(--color-foreground)',
                  marginBottom: '4px'
                }}>
                  Paramètres
                </h2>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-muted-foreground)'
                }}>
                  Entrez les détails de votre hypothèque
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              <Input
                label="Prix d'achat"
                type="number"
                placeholder="Ex: 500000"
                value={formData.purchasePrice}
                onChange={(e) => handleChange('purchasePrice', e.target.value)}
                required
                min="0"
              />

              <Input
                label="Mise de fonds (%)"
                type="number"
                placeholder="Ex: 20"
                value={formData.downPaymentPercent}
                onChange={(e) => handleChange('downPaymentPercent', e.target.value)}
                required
                min="0"
                max="100"
              />

              <Input
                label="Taux d'intérêt (%)"
                type="number"
                placeholder="Ex: 5.5"
                value={formData.interestRate}
                onChange={(e) => handleChange('interestRate', e.target.value)}
                required
                min="0"
                max="20"
                step="0.1"
              />

              <Input
                label="Période d'amortissement (ans)"
                type="number"
                placeholder="Ex: 25"
                value={formData.amortizationPeriod}
                onChange={(e) => handleChange('amortizationPeriod', e.target.value)}
                required
                min="0"
                max="50"
                step="1"
              />

              <Select
                label="Fréquence de paiement"
                options={paymentFrequencies}
                value={formData.paymentFrequency}
                onChange={(value) => handleChange('paymentFrequency', value)}
                required
              />

              <Input
                label="Prime SCHL (%)"
                type="number"
                placeholder="Optionnel (ex: 4)"
                value={formData.schlPremium}
                onChange={(e) => handleChange('schlPremium', e.target.value)}
                min="0"
                max="100"
                step="0.1"
                description="% du montant du prêt (après mise de fonds)"
              />
            </div>
          </div>

          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            padding: 'var(--spacing-lg)'
          }}>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              Résultats
            </h2>

            {results ? (

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                <div style={{
                  padding: 'var(--spacing-md)',
                  background: 'var(--color-primary)/10',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-primary)/20'
                }}>
                  <p style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.75rem',
                    color: 'var(--color-muted-foreground)',
                    marginBottom: '4px'
                  }}>
                    Paiement {formData.paymentFrequency === 'monthly' ? 'mensuel' : formData.paymentFrequency === 'biweekly' ? 'aux 2 semaines' : 'hebdomadaire'}
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'var(--color-primary)'
                  }}>
                    {formatCurrencyWithCents(results.paymentAmounts[formData.paymentFrequency])}
                  </p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 'var(--spacing-sm)'
                }}>
                  <div style={{
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-muted)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      marginBottom: '4px'
                    }}>
                      Prix d'achat
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-foreground)'
                    }}>
                      {formatCurrency(results.purchasePrice)}
                    </p>
                  </div>

                  <div style={{
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-muted)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      marginBottom: '4px'
                    }}>
                      Mise de fonds
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-foreground)'
                    }}>
                      {formatCurrency(results.downPaymentAmount)}
                    </p>
                  </div>

                  {results.schlPremiumAmount > 0 && (
                    <div style={{
                      padding: 'var(--spacing-sm)',
                      borderBottom: '1px solid var(--color-border)'
                    }}>
                      <p style={{
                        fontFamily: 'var(--font-caption)',
                        fontSize: '0.75rem',
                        color: 'var(--color-muted-foreground)',
                        marginBottom: '4px'
                      }}>
                        Montant hypothécaire (base)
                      </p>
                      <p style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--color-foreground)'
                      }}>
                        {formatCurrency(results.baseMortgageAmount)}
                      </p>
                    </div>
                  )}
                  {results.schlPremiumAmount > 0 && (
                    <div style={{
                      padding: 'var(--spacing-sm)',
                      borderBottom: '1px solid var(--color-border)'
                    }}>
                      <p style={{
                        fontFamily: 'var(--font-caption)',
                        fontSize: '0.75rem',
                        color: 'var(--color-muted-foreground)',
                        marginBottom: '4px'
                      }}>
                        Prime SCHL
                      </p>
                      <p style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--color-foreground)'
                      }}>
                        {formatCurrency(results.schlPremiumAmount)}
                      </p>
                    </div>
                  )}
                  <div style={{
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-muted)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      marginBottom: '4px'
                    }}>
                      Montant hypothécaire {results.schlPremiumAmount > 0 ? '(avec prime SCHL)' : ''}
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-foreground)'
                    }}>
                      {formatCurrency(results.mortgageAmount)}
                    </p>
                  </div>

                  <div style={{
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-muted)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      marginBottom: '4px'
                    }}>
                      Intérêts totaux
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-foreground)'
                    }}>
                      {formatCurrency(results.totalInterest)}
                    </p>
                  </div>
                </div>

                <div style={{
                  padding: 'var(--spacing-md)',
                  background: 'var(--color-muted)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)'
                }}>
                  <p style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.75rem',
                    color: 'var(--color-muted-foreground)',
                    marginBottom: '4px'
                  }}>
                    Montant total payé ({results.amortizationYears} ans)
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)'
                  }}>
                    {formatCurrency(results.totalAmount)}
                  </p>
                </div>

                {formData.paymentFrequency !== 'monthly' && (
                  <div style={{
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-success)/10',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-success)/20'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-muted-foreground)',
                      marginBottom: '4px'
                    }}>
                      Équivalent mensuel
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-success)'
                    }}>
                      {formatCurrencyWithCents(results.monthlyPayment)}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: 'var(--spacing-lg)',
                textAlign: 'center',
                color: 'var(--color-muted-foreground)'
              }}>
                <Icon name="Calculator" size={48} style={{ marginBottom: 'var(--spacing-md)', opacity: 0.3 }} />
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem'
                }}>
                  Entrez le prix d'achat et le taux d'intérêt pour voir les résultats
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Amortization Schedule Table */}
        {results && results.amortizationSchedule && results.amortizationSchedule.length > 0 && (
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            padding: 'var(--spacing-xl)',
            marginTop: 'var(--spacing-lg)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--spacing-lg)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-primary)/10',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon name="Table" size={20} color="var(--color-primary)" />
                </div>
                <h2 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: 'var(--color-foreground)',
                  margin: 0
                }}>
                  Tableau d'amortissement
                </h2>
              </div>
            </div>
            
            <div style={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: '600px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--color-background)'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem'
              }}>
                <thead>
                  <tr>
                    <th style={{
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: 'var(--color-foreground)',
                      background: 'var(--color-muted)',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderBottom: '2px solid var(--color-border)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Année
                    </th>
                    <th style={{
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'var(--color-foreground)',
                      background: 'var(--color-muted)',
                      borderBottom: '2px solid var(--color-border)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Intérêt remboursé
                    </th>
                    <th style={{
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'var(--color-foreground)',
                      background: 'var(--color-muted)',
                      borderBottom: '2px solid var(--color-border)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Capital remboursé
                    </th>
                    <th style={{
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'var(--color-foreground)',
                      background: 'var(--color-muted)',
                      borderBottom: '2px solid var(--color-border)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Solde restant
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.amortizationSchedule.map((row, index) => {
                    const isLastRow = index === results.amortizationSchedule.length - 1;
                    return (
                      <tr 
                        key={row.year}
                        style={{
                          borderBottom: isLastRow ? 'none' : '1px solid var(--color-border)',
                          background: index % 2 === 0 ? 'transparent' : 'var(--color-muted)/20',
                          transition: 'background-color 0.2s ease',
                          cursor: 'default'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--color-primary)/5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = index % 2 === 0 ? 'transparent' : 'var(--color-muted)/20';
                        }}
                      >
                        <td style={{
                          padding: 'var(--spacing-md) var(--spacing-lg)',
                          fontWeight: 600,
                          color: 'var(--color-foreground)',
                          position: 'sticky',
                          left: 0,
                          background: index % 2 === 0 ? 'var(--color-background)' : 'var(--color-muted)/20',
                          zIndex: 1,
                          borderRight: '1px solid var(--color-border)',
                          fontFamily: 'var(--font-heading)'
                        }}>
                          {row.year}
                        </td>
                        <td style={{
                          padding: 'var(--spacing-md) var(--spacing-lg)',
                          textAlign: 'right',
                          color: 'var(--color-destructive)',
                          fontFamily: 'var(--font-data)',
                          fontWeight: 500
                        }}>
                          {formatCurrencyWithCents(row.interestPaid)}
                        </td>
                        <td style={{
                          padding: 'var(--spacing-md) var(--spacing-lg)',
                          textAlign: 'right',
                          color: 'var(--color-success)',
                          fontFamily: 'var(--font-data)',
                          fontWeight: 500
                        }}>
                          {formatCurrencyWithCents(row.principalPaid)}
                        </td>
                        <td style={{
                          padding: 'var(--spacing-md) var(--spacing-lg)',
                          textAlign: 'right',
                          color: 'var(--color-foreground)',
                          fontFamily: 'var(--font-data)',
                          fontWeight: 600
                        }}>
                          {formatCurrencyWithCents(row.balance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Summary footer */}
            <div style={{
              marginTop: 'var(--spacing-lg)',
              padding: 'var(--spacing-md)',
              background: 'var(--color-muted)/30',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--spacing-md)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}>
                <Icon name="Info" size={16} color="var(--color-muted-foreground)" />
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-muted-foreground)'
                }}>
                  {results.amortizationSchedule.length} {results.amortizationSchedule.length === 1 ? 'année' : 'années'} d'amortissement
                </span>
              </div>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-lg)',
                flexWrap: 'wrap'
              }}>
                <div>
                  <span style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.75rem',
                    color: 'var(--color-muted-foreground)',
                    marginRight: 'var(--spacing-xs)'
                  }}>
                    Total intérêts:
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-destructive)'
                  }}>
                    {formatCurrencyWithCents(results.totalInterest)}
                  </span>
                </div>
                <div>
                  <span style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.75rem',
                    color: 'var(--color-muted-foreground)',
                    marginRight: 'var(--spacing-xs)'
                  }}>
                    Total remboursé:
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)'
                  }}>
                    {formatCurrencyWithCents(results.totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MortgageCalculator;
